import React from 'react'
import { Link } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import { BLOCKS, MARKS, INLINES } from '@contentful/rich-text-types'
import ReferenceComponentRenderer from '../ReferenceComponentRenderer'
import HeadlineBlockCard from '../Cards/HeadlineBlockCard'
import { isValidUrl } from './URLRenderer'
import EmailText from './EmailText'

// For migrating to the newest Gatsby (TECH-2675), we discovered that there are some
// rich text fields in Contentful, and that they need to be rendered through a more rigorous
// system than content[0].content[0].  This is that system, and it is bare minimum.  Rich text
// can do more than this, and we should set it up appropriately where we intend to use it.
export function renderRawHack(content) {
  const options = {
    renderMark: {
      [MARKS.BOLD]: text => <strong>{text}</strong>,
      [MARKS.ITALIC]: text => <i>{text}</i>
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => children
    }
  }

  return content ? renderRichText(content, options).join(' ') : null
}

// There should be a syntax for links in the rich text language, but this is the markup we did first.
// later, migrate to the standard syntax and migrate contentful where our nonstandard syntax is used to use
// the standard instead.
// TODO https://avantinc.atlassian.net/browse/TECH-2727
export function linkHack(notQuiteRichTextField, linkClass = 'flat-link--white') {
  const rt = notQuiteRichTextField?.raw ? notQuiteRichTextField.raw : notQuiteRichTextField
  return rt
    ? rt.split(/[{}]/).map(el => {
        if (el.indexOf('|') > -1) {
          const parts = el.split('|')
          if (parts[0] === 'email') {
            return <EmailText key={parts[1]} email={parts[1]} linkClass={linkClass} />
          }
          if (isValidUrl(parts[1])) {
            return (
              <a key={parts[0]} href={parts[1]} className={linkClass}>
                {parts[0]}
              </a>
            )
          }
          return (
            <Link key={parts[0]} className={linkClass} to={parts[1]}>
              {parts[0]}
            </Link>
          )
        }
        return el
      })
    : null
}

// see also https://www.contentful.com/developers/docs/tutorials/general/rich-text-and-gatsby/
export function renderRawWithLinksHack(richTextField, linkClass = 'flat-link--white') {
  const rt = renderRawHack(richTextField)
  return rt ? linkHack(rt, linkClass) : null
}

export function renderReactComponents(object) {
  const { richText, onEvent } = object

  const options = {
    renderMark: {
      [MARKS.BOLD]: text => <strong>{text}</strong>,
      [MARKS.ITALIC]: text => <i>{text}</i>
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => {
        const allStrings = children.every(child => typeof child === 'string')
        return allStrings ? <p>{children}</p> : <div>{children}</div>
      },
      [BLOCKS.HEADING_1]: node => {
        const text = node.content[0].value
        return <HeadlineBlockCard text={text} />
      },
      [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
      [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
      [BLOCKS.HR]: () => <hr />,
      [INLINES.EMBEDDED_ENTRY]: node => {
        const { target } = node.data
        return (
          <GatsbyImage
            image={getImage(target.image)}
            alt={target.altText}
            className="inline-image"
          />
        )
      },
      [BLOCKS.EMBEDDED_ENTRY]: node => {
        const { target } = node.data
        const { __typename } = target
        return (
          <ReferenceComponentRenderer
            contentType={__typename}
            node={target}
            insideRichText
            event={onEvent}
          />
        )
      }
    }
  }
  return richText ? renderRichText(richText, options) : null
}

export function renderParagraphWithLinks(richText) {
  const options = {
    renderMark: {
      [MARKS.BOLD]: text => <strong>{text}</strong>,
      [MARKS.ITALIC]: text => <i>{text}</i>,
      [MARKS.UNDERLINE]: text => <u>{text}</u>
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
      [BLOCKS.HEADING_1]: (node, children) => <h1>{children}</h1>,
      [BLOCKS.HEADING_2]: (node, children) => <h2>{children}</h2>,
      [BLOCKS.HEADING_3]: (node, children) => <h3>{children}</h3>,
      [BLOCKS.HR]: () => <hr />,
      [INLINES.HYPERLINK]: (node, children) => <a href={node.data.uri}>{children}</a>,
      [INLINES.EMBEDDED_ENTRY]: node => {
        const { target } = node.data
        const { __typename } = target
        return <ReferenceComponentRenderer contentType={__typename} node={target} />
      }
    }
  }
  return richText ? renderRichText(richText, options) : null
}
